import React, { useRef, useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LayoutElement from './LayoutElement';
import SubFieldSelector from '../SubFieldSelector';
import {
  ELEMENT_TYPES,
  LAYOUT_ZONES,
  getPageDimensions,
  mmToPx,
  pxToMm,
  DEFAULT_FONT
} from '../../utils/constants';
import './style.css';

export default function LayoutEditor({
  report,
  entities,
  selectedElement,
  previewMode,
  previewData,
  gridSettings,
  onGridSettingsChange,
  onAddElement,
  onUpdateElement,
  onRemoveElement,
  onSelectElement
}) {
  const [dragOverZone, setDragOverZone] = useState(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  // Scroll to top when component mounts or preview mode changes
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [previewMode]);

  // State for SubFieldSelector (externalEntity fields)
  const [pendingExternalField, setPendingExternalField] = useState(null);
  const [pendingDropInfo, setPendingDropInfo] = useState(null);

  // State for query column type choice modal
  const [showTypeChoiceModal, setShowTypeChoiceModal] = useState(false);
  const [pendingQueryColumn, setPendingQueryColumn] = useState(null);

  // Get page dimensions
  const { format, orientation, margins } = report.pageSettings;
  const pageDims = getPageDimensions(format, orientation);
  const pageWidthPx = mmToPx(pageDims.width);
  const pageHeightPx = mmToPx(pageDims.height);

  // Calculate content area (body is the entire content area now)
  const contentWidthMm = pageDims.width - margins.left - margins.right;
  const contentHeightMm = pageDims.height - margins.top - margins.bottom;

  // Estimated row height for pagination (in mm)
  const ESTIMATED_ROW_HEIGHT_MM = 8;
  const TABLE_HEADER_HEIGHT_MM = 9;
  const BOTTOM_SAFETY_MARGIN_MM = 5; // Safety margin to ensure rows don't overflow

  // Calculate pages for preview mode with pagination
  const calculatePages = () => {
    if (!previewMode || !previewData) return [{ startRow: 0, endRow: Infinity }];

    const bodyElements = report.layout.body?.elements || [];
    const tableElement = bodyElements.find(el => el.type === ELEMENT_TYPES.TABLE);

    if (!tableElement) return [{ startRow: 0, endRow: Infinity }];

    // Get data for this table
    const queryId = tableElement.queryId || tableElement.columns?.[0]?.queryId || tableElement.columns?.[0]?.field?.queryId;
    const tableData = queryId && previewData[queryId]?.data ? previewData[queryId].data : [];

    if (tableData.length === 0) return [{ startRow: 0, endRow: Infinity }];

    // Check if header is visible
    const showHeader = tableElement.style?.showHeader !== false;
    const headerHeight = showHeader ? TABLE_HEADER_HEIGHT_MM : 0;

    // Calculate available height for table on each page (accounting for bottom safety margin)
    const tableY = tableElement.y || 0;
    const availableHeightFirstPage = contentHeightMm - tableY - headerHeight - BOTTOM_SAFETY_MARGIN_MM;
    const availableHeightOtherPages = contentHeightMm - headerHeight - BOTTOM_SAFETY_MARGIN_MM;

    // Calculate rows per page
    const rowsFirstPage = Math.max(1, Math.floor(availableHeightFirstPage / ESTIMATED_ROW_HEIGHT_MM));
    const rowsPerPage = Math.max(1, Math.floor(availableHeightOtherPages / ESTIMATED_ROW_HEIGHT_MM));

    const pages = [];
    let currentRow = 0;

    // First page
    const endRowFirstPage = Math.min(currentRow + rowsFirstPage, tableData.length);
    pages.push({ startRow: currentRow, endRow: endRowFirstPage, isFirst: true });
    currentRow = endRowFirstPage;

    // Additional pages
    while (currentRow < tableData.length) {
      const endRow = Math.min(currentRow + rowsPerPage, tableData.length);
      pages.push({ startRow: currentRow, endRow: endRow, isFirst: false });
      currentRow = endRow;
    }

    return pages;
  };

  const pages = previewMode ? calculatePages() : [{ startRow: 0, endRow: Infinity }];

  // Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDragOverZone(LAYOUT_ZONES.BODY);
  };

  // Handle drag leave
  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverZone(null);
    }
  };

  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOverZone(null);

    try {
      const data = e.dataTransfer.getData('application/json');
      if (!data) return;

      const dragData = JSON.parse(data);
      const zoneRect = e.currentTarget.getBoundingClientRect();
      const x = pxToMm(e.clientX - zoneRect.left);
      const y = pxToMm(e.clientY - zoneRect.top);

      // Handle query_column drops (from QueryPanel)
      if (dragData.type === 'query_column') {
        const columnData = {
          ...dragData,
          dropZone: LAYOUT_ZONES.BODY,
          dropX: Math.max(0, x),
          dropY: Math.max(0, y)
        };

        // If multi-row query, show choice modal
        if (dragData.isMultiRow) {
          setPendingQueryColumn(columnData);
          setShowTypeChoiceModal(true);
          return;
        }

        // Single row - create data field directly
        createDataFieldFromQueryColumn(columnData);
        return;
      }

      // Handle regular field drops (legacy support)
      const field = dragData;

      // Check if this is an externalEntity field
      const isExternalEntity = ['externalEntity', 'deepExternalEntity'].includes(field.type);

      if (isExternalEntity && field.options?.entity) {
        setPendingExternalField(field);
        setPendingDropInfo({ x: Math.max(0, x), y: Math.max(0, y) });
        return;
      }

      // Create new data field element
      const newElement = {
        type: ELEMENT_TYPES.DATA_FIELD,
        x: Math.max(0, x),
        y: Math.max(0, y),
        width: 40,
        height: 8,
        field: field,
        style: {
          ...DEFAULT_FONT,
          textAlign: 'left',
          backgroundColor: 'transparent',
          borderWidth: 0,
          borderStyle: 'none',
          borderColor: '#000000',
          padding: 2
        }
      };

      onAddElement(LAYOUT_ZONES.BODY, newElement);
    } catch (error) {
      console.error('Error handling drop:', error);
    }
  };

  // Create data field from query column
  const createDataFieldFromQueryColumn = (columnData) => {
    const newElement = {
      type: ELEMENT_TYPES.DATA_FIELD,
      x: columnData.dropX,
      y: columnData.dropY,
      width: 40,
      height: 8,
      field: {
        id: `query_${columnData.queryId}_${columnData.column.name}`,
        name: columnData.column.name,
        label: columnData.column.label || columnData.column.name,
        type: 'query_field',
        queryId: columnData.queryId,
        queryName: columnData.queryName
      },
      style: {
        ...DEFAULT_FONT,
        textAlign: 'left',
        backgroundColor: 'transparent',
        borderWidth: 0,
        borderStyle: 'none',
        borderColor: '#000000',
        padding: 2
      }
    };

    onAddElement(LAYOUT_ZONES.BODY, newElement);
  };

  // Create table from query column
  const createTableFromQueryColumn = (columnData) => {
    const newElement = {
      type: ELEMENT_TYPES.TABLE,
      x: 0,
      y: columnData.dropY,
      width: contentWidthMm,
      height: 'auto',
      queryId: columnData.queryId,
      queryName: columnData.queryName,
      columns: [{
        id: 'col_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
        field: {
          id: `query_${columnData.queryId}_${columnData.column.name}`,
          name: columnData.column.name,
          label: columnData.column.label || columnData.column.name,
          type: 'query_field',
          queryId: columnData.queryId
        },
        width: 'auto',
        headerStyle: { ...DEFAULT_FONT, fontWeight: 'bold' },
        cellStyle: { ...DEFAULT_FONT }
      }],
      style: {
        headerBackground: '#f8f9fa',
        rowBackground: '#ffffff',
        alternateRowBackground: '#f8f9fa',
        borderWidth: 1,
        borderColor: '#ddd'
      }
    };

    onAddElement(LAYOUT_ZONES.BODY, newElement);
  };

  // Find existing tables with the same queryId
  const findExistingTablesForQuery = (queryId) => {
    const tables = [];
    const elements = report.layout.body?.elements || [];
    elements.forEach(el => {
      if (el.type === ELEMENT_TYPES.TABLE && el.queryId === queryId) {
        tables.push({ ...el, zone: LAYOUT_ZONES.BODY });
      }
    });
    return tables;
  };

  // Add column to existing table
  const addColumnToExistingTable = (columnData, table) => {
    const newColumn = {
      id: 'col_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
      field: {
        id: `query_${columnData.queryId}_${columnData.column.name}`,
        name: columnData.column.name,
        label: columnData.column.label || columnData.column.name,
        type: 'query_field',
        queryId: columnData.queryId
      },
      width: 'auto',
      headerStyle: { ...DEFAULT_FONT, fontWeight: 'bold' },
      cellStyle: { ...DEFAULT_FONT }
    };

    const updatedColumns = [...(table.columns || []), newColumn];
    onUpdateElement(LAYOUT_ZONES.BODY, table.id, { columns: updatedColumns });
  };

  // Handle type choice for query column
  const handleTypeChoice = (choice, existingTable = null) => {
    if (!pendingQueryColumn) return;

    if (choice === 'DATA_FIELD') {
      createDataFieldFromQueryColumn(pendingQueryColumn);
    } else if (choice === 'TABLE') {
      createTableFromQueryColumn(pendingQueryColumn);
    } else if (choice === 'ADD_TO_TABLE' && existingTable) {
      addColumnToExistingTable(pendingQueryColumn, existingTable);
    }

    setPendingQueryColumn(null);
    setShowTypeChoiceModal(false);
  };

  // Cancel type choice
  const handleTypeChoiceCancel = () => {
    setPendingQueryColumn(null);
    setShowTypeChoiceModal(false);
  };

  // Get existing tables for pending query column
  const existingTablesForQuery = pendingQueryColumn
    ? findExistingTablesForQuery(pendingQueryColumn.queryId)
    : [];

  // Handle subfield selection for externalEntity
  const handleSubFieldSelect = (fieldWithSubField) => {
    if (!pendingDropInfo) return;

    const { x, y } = pendingDropInfo;

    const newElement = {
      type: ELEMENT_TYPES.DATA_FIELD,
      x: x,
      y: y,
      width: 40,
      height: 8,
      field: fieldWithSubField,
      style: {
        ...DEFAULT_FONT,
        textAlign: 'left',
        backgroundColor: 'transparent',
        borderWidth: 0,
        borderStyle: 'none',
        borderColor: '#000000',
        padding: 2
      }
    };

    onAddElement(LAYOUT_ZONES.BODY, newElement);
    setPendingExternalField(null);
    setPendingDropInfo(null);
  };

  // Cancel subfield selection
  const handleSubFieldCancel = () => {
    setPendingExternalField(null);
    setPendingDropInfo(null);
  };

  // Handle canvas click (deselect)
  const handleCanvasClick = (e) => {
    if (e.target === e.currentTarget || e.target.classList.contains('canvas-zone')) {
      onSelectElement(null, null);
    }
  };

  // Handle element selection
  const handleElementSelect = (elementId, e) => {
    e.stopPropagation();
    onSelectElement(LAYOUT_ZONES.BODY, elementId);
  };

  // Handle element move
  const handleElementMove = (elementId, newX, newY) => {
    onUpdateElement(LAYOUT_ZONES.BODY, elementId, { x: newX, y: newY });
  };

  // Handle element resize
  const handleElementResize = (elementId, newWidth, newHeight) => {
    onUpdateElement(LAYOUT_ZONES.BODY, elementId, { width: newWidth, height: newHeight });
  };

  // Handle element delete
  const handleElementDelete = (elementId) => {
    onRemoveElement(LAYOUT_ZONES.BODY, elementId);
  };

  // Add text element
  const addTextElement = () => {
    const newElement = {
      type: ELEMENT_TYPES.TEXT,
      x: 10,
      y: 10,
      width: 50,
      height: 8,
      content: 'Testo',
      style: {
        ...DEFAULT_FONT,
        textAlign: 'left',
        backgroundColor: 'transparent',
        borderWidth: 0,
        borderStyle: 'none',
        borderColor: '#000000',
        padding: 2
      }
    };
    onAddElement(LAYOUT_ZONES.BODY, newElement);
  };

  // Add table element
  const addTableElement = () => {
    const newElement = {
      type: ELEMENT_TYPES.TABLE,
      x: 0,
      y: 10,
      width: contentWidthMm,
      height: 'auto',
      columns: [],
      style: {
        headerBackground: '#f8f9fa',
        rowBackground: '#ffffff',
        alternateRowBackground: '#f8f9fa',
        borderWidth: 1,
        borderColor: '#ddd'
      }
    };
    onAddElement(LAYOUT_ZONES.BODY, newElement);
  };

  // Add rectangle element
  const addRectangleElement = () => {
    const newElement = {
      type: ELEMENT_TYPES.RECTANGLE,
      x: 10,
      y: 10,
      width: 40,
      height: 20,
      style: {
        backgroundColor: 'transparent',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#000000'
      }
    };
    onAddElement(LAYOUT_ZONES.BODY, newElement);
  };

  // Add image element
  const addImageElement = () => {
    const newElement = {
      type: ELEMENT_TYPES.IMAGE,
      x: 10,
      y: 10,
      width: 30,
      height: 30,
      src: null, // Will be set to base64 when image is uploaded
      style: {
        objectFit: 'contain'
      }
    };
    onAddElement(LAYOUT_ZONES.BODY, newElement);
  };

  // Get display value for preview
  const getPreviewValue = (element, rowIndex = 0) => {
    if (element.type === ELEMENT_TYPES.TEXT) {
      return element.content;
    }

    if (element.type === ELEMENT_TYPES.PAGE_NUMBER) {
      return element.format.replace('{page}', '1').replace('{pages}', '1');
    }

    // Get data for this element's query
    const getElementData = () => {
      if (!previewData || typeof previewData !== 'object') return [];
      const queryId = element.queryId || element.field?.queryId;
      if (queryId && previewData[queryId]?.data) {
        return previewData[queryId].data;
      }
      if (Array.isArray(previewData)) {
        return previewData;
      }
      return [];
    };

    const elementData = getElementData();

    if (element.type === ELEMENT_TYPES.DATA_FIELD && previewMode && elementData.length > 0) {
      const row = elementData[rowIndex] || elementData[0];
      const fieldName = element.field?.name || element.field?.column?.name;
      if (row && fieldName && row[fieldName] !== undefined) {
        const fieldValue = row[fieldName];

        if (element.field.subField && fieldValue && typeof fieldValue === 'object') {
          const subValue = fieldValue[element.field.subField];
          return subValue !== undefined ? String(subValue) : '';
        }

        if (['externalEntity', 'deepExternalEntity'].includes(element.field.type) && fieldValue && typeof fieldValue === 'object') {
          if (element.field.options?.labelField && fieldValue[element.field.options.labelField] !== undefined) {
            return String(fieldValue[element.field.options.labelField]);
          }
          for (const key of ['name', 'title', 'label', 'id']) {
            if (fieldValue[key] !== undefined) {
              return String(fieldValue[key]);
            }
          }
          return '[oggetto]';
        }

        return String(fieldValue);
      }
    }

    if (element.field) {
      if (element.field.displayLabel) {
        return `{${element.field.displayLabel}}`;
      }
      return `{${element.field.label}}`;
    }
    return '';
  };

  // Render body elements
  const renderBodyElements = (pageInfo) => {
    const elements = report.layout.body?.elements || [];

    // In preview mode, filter elements based on page:
    // - Tables are rendered on all pages (with pagination)
    // - Other elements (text, data fields, etc.) only appear on page 1
    const filteredElements = previewMode
      ? elements.filter(element => {
          // Tables are always rendered (pagination handled internally)
          if (element.type === ELEMENT_TYPES.TABLE) return true;
          // Other elements only on first page
          return pageInfo.pageIndex === 0;
        })
      : elements;

    return filteredElements.map(element => (
      <LayoutElement
        key={element.id}
        element={element}
        zone={LAYOUT_ZONES.BODY}
        isSelected={selectedElement?.zone === LAYOUT_ZONES.BODY && selectedElement?.id === element.id}
        previewMode={previewMode}
        contentWidthMm={contentWidthMm}
        zoneHeightMm={contentHeightMm}
        displayValue={getPreviewValue(element)}
        previewData={previewData}
        entities={entities}
        pageInfo={pageInfo}
        gridSettings={gridSettings}
        onSelect={(e) => handleElementSelect(element.id, e)}
        onMove={(x, y) => handleElementMove(element.id, x, y)}
        onResize={(w, h) => handleElementResize(element.id, w, h)}
        onDelete={() => handleElementDelete(element.id)}
      />
    ));
  };

  // Inline styles for toolbar
  const toolbarContainerStyle = {
    padding: '12px 16px',
    background: '#fff',
    borderBottom: '2px solid #dee2e6',
    flexShrink: 0
  };

  const toolbarRowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap'
  };

  const toolbarLabelStyle = {
    fontSize: '13px',
    fontWeight: 600,
    color: '#495057',
    marginRight: '8px'
  };

  const toolbarBtnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 16px',
    border: '1px solid #dee2e6',
    background: '#fff',
    borderRadius: '4px',
    fontSize: '13px',
    color: '#495057',
    cursor: 'pointer',
    fontWeight: 500,
    transition: 'all 0.15s'
  };

  return (
    <div className="layout-editor">
      {/* Element Toolbar */}
      <div style={toolbarContainerStyle}>
        <div style={toolbarRowStyle}>
          <span style={toolbarLabelStyle}>
            <FontAwesomeIcon icon="plus-circle" style={{ marginRight: '6px', color: '#007bff' }} />
            Aggiungi:
          </span>

          <button
            style={toolbarBtnStyle}
            onClick={addTableElement}
            title="Aggiungi una tabella vuota"
          >
            <FontAwesomeIcon icon="table" />
            Tabella
          </button>

          <button
            style={toolbarBtnStyle}
            onClick={addTextElement}
            title="Aggiungi testo statico"
          >
            <FontAwesomeIcon icon="font" />
            Testo
          </button>

          <button
            style={toolbarBtnStyle}
            onClick={addRectangleElement}
            title="Aggiungi rettangolo"
          >
            <FontAwesomeIcon icon="square" />
            Rettangolo
          </button>

          <button
            style={toolbarBtnStyle}
            onClick={addImageElement}
            title="Aggiungi immagine"
          >
            <FontAwesomeIcon icon="image" />
            Immagine
          </button>

          {/* Separator */}
          <div style={{ width: '1px', height: '24px', background: '#dee2e6', margin: '0 8px' }} />

          {/* Grid controls */}
          <span style={{ ...toolbarLabelStyle, marginLeft: '8px' }}>
            <FontAwesomeIcon icon="th" style={{ marginRight: '6px', color: '#6c757d' }} />
            Griglia:
          </span>

          <button
            style={{
              ...toolbarBtnStyle,
              background: gridSettings?.visible ? '#e7f3ff' : '#fff',
              borderColor: gridSettings?.visible ? '#007bff' : '#dee2e6',
              color: gridSettings?.visible ? '#007bff' : '#495057'
            }}
            onClick={() => onGridSettingsChange?.({ ...gridSettings, visible: !gridSettings?.visible })}
            title="Mostra/Nascondi griglia"
          >
            <FontAwesomeIcon icon="eye" />
          </button>

          <button
            style={{
              ...toolbarBtnStyle,
              background: gridSettings?.snapEnabled ? '#e7f3ff' : '#fff',
              borderColor: gridSettings?.snapEnabled ? '#007bff' : '#dee2e6',
              color: gridSettings?.snapEnabled ? '#007bff' : '#495057'
            }}
            onClick={() => onGridSettingsChange?.({ ...gridSettings, snapEnabled: !gridSettings?.snapEnabled })}
            title="Attiva/Disattiva snap alla griglia"
          >
            <FontAwesomeIcon icon="magnet" />
          </button>

          <select
            style={{
              padding: '8px 12px',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              fontSize: '13px',
              background: '#fff',
              cursor: 'pointer'
            }}
            value={gridSettings?.size || 5}
            onChange={(e) => onGridSettingsChange?.({ ...gridSettings, size: parseInt(e.target.value) })}
            title="Dimensione griglia"
          >
            <option value={2}>2mm</option>
            <option value={5}>5mm</option>
            <option value={10}>10mm</option>
          </select>
        </div>
      </div>

      {/* Canvas Container */}
      <div ref={containerRef} className="canvas-container" onClick={handleCanvasClick}>
        {/* Render multiple pages in preview mode */}
        {pages.map((pageInfo, pageIndex) => (
          <div
            key={pageIndex}
            className="canvas-wrapper"
            style={{
              width: pageWidthPx,
              minHeight: pageHeightPx
            }}
          >
            {/* Page number indicator in preview mode */}
            {previewMode && pages.length > 1 && (
              <div className="page-number-indicator">
                Pagina {pageIndex + 1} di {pages.length}
              </div>
            )}

            {/* Page with margins */}
            <div
              ref={pageIndex === 0 ? canvasRef : null}
              className="canvas-page"
              style={{
                width: pageWidthPx,
                minHeight: pageHeightPx,
                padding: `${mmToPx(margins.top)}px ${mmToPx(margins.right)}px ${mmToPx(margins.bottom)}px ${mmToPx(margins.left)}px`
              }}
            >
              {/* Body Zone (entire content area) */}
              <div
                className={`canvas-zone zone-body ${dragOverZone === LAYOUT_ZONES.BODY ? 'drag-over' : ''}`}
                style={{
                  minHeight: mmToPx(contentHeightMm),
                  ...(gridSettings?.visible && !previewMode ? {
                    backgroundImage: `
                      linear-gradient(to right, rgba(0, 123, 255, 0.1) 1px, transparent 1px),
                      linear-gradient(to bottom, rgba(0, 123, 255, 0.1) 1px, transparent 1px)
                    `,
                    backgroundSize: `${mmToPx(gridSettings.size)}px ${mmToPx(gridSettings.size)}px`
                  } : {})
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                {renderBodyElements({ ...pageInfo, pageIndex, totalPages: pages.length })}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Data Preview Info */}
      {previewMode && previewData && Object.keys(previewData).length > 0 && (
        <div className="data-preview-info">
          <FontAwesomeIcon icon="info-circle" />
          Anteprima: {Object.values(previewData).reduce((sum, q) => sum + (q.data?.length || 0), 0)} record da {Object.keys(previewData).length} query
        </div>
      )}

      {/* SubFieldSelector for externalEntity fields */}
      <SubFieldSelector
        isOpen={pendingExternalField !== null}
        field={pendingExternalField}
        onSelect={handleSubFieldSelect}
        onCancel={handleSubFieldCancel}
      />

      {/* Type Choice Modal for query columns */}
      {showTypeChoiceModal && pendingQueryColumn && (
        <div className="type-choice-modal-overlay" onClick={handleTypeChoiceCancel}>
          <div className="type-choice-modal" onClick={e => e.stopPropagation()}>
            <div className="type-choice-header">
              <h4>
                <FontAwesomeIcon icon="question-circle" style={{ marginRight: '8px', color: '#007bff' }} />
                Scegli tipo elemento
              </h4>
            </div>
            <div className="type-choice-body">
              <p className="type-choice-info">
                La query restituisce <strong>più righe</strong>. Come vuoi visualizzare il campo <strong>"{pendingQueryColumn.column.name}"</strong>?
              </p>

              <div className="type-choice-options">
                {existingTablesForQuery.length > 0 && (
                  <button
                    className="type-choice-btn type-choice-primary"
                    onClick={() => handleTypeChoice('ADD_TO_TABLE', existingTablesForQuery[0])}
                  >
                    <div className="type-choice-icon">
                      <FontAwesomeIcon icon="plus" />
                    </div>
                    <div className="type-choice-info-box">
                      <strong>Aggiungi a Tabella Esistente</strong>
                      <span>Aggiungi come nuova colonna alla tabella già presente</span>
                    </div>
                  </button>
                )}

                <button
                  className={`type-choice-btn ${existingTablesForQuery.length === 0 ? 'type-choice-primary' : ''}`}
                  onClick={() => handleTypeChoice('TABLE')}
                >
                  <div className="type-choice-icon">
                    <FontAwesomeIcon icon="table" />
                  </div>
                  <div className="type-choice-info-box">
                    <strong>Nuova Tabella</strong>
                    <span>Crea una nuova tabella con questa colonna</span>
                  </div>
                </button>

                <button
                  className="type-choice-btn"
                  onClick={() => handleTypeChoice('DATA_FIELD')}
                >
                  <div className="type-choice-icon">
                    <FontAwesomeIcon icon="font" />
                  </div>
                  <div className="type-choice-info-box">
                    <strong>Campo Singolo</strong>
                    <span>Mostra solo il valore della prima riga</span>
                  </div>
                </button>
              </div>
            </div>
            <div className="type-choice-footer">
              <button className="type-choice-cancel" onClick={handleTypeChoiceCancel}>
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
